import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { getAdminToken } from "../components/Auth";
import { toast } from "sonner";
import { Save, RotateCcw, MessageSquare, CreditCard, Users, Cpu, DollarSign, ExternalLink, Thermometer, Eye, EyeOff, ShieldOff, X, Plus, Server, Download, Monitor, Upload, Copy, Link2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import WhatsAppBackup from "../components/WhatsAppBackup";
import WebhookLock from "../components/WebhookLock";

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [machineTypes, setMachineTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fleetProInfo, setFleetProInfo] = useState(null);
  const [uploadingFleetPro, setUploadingFleetPro] = useState(false);
  // Lives in bot_settings, not the farm settings object above, so it loads and
  // saves on its own rather than through "Save Changes" at the top.
  const [fleetMonitorPhone, setFleetMonitorPhone] = useState("");
  const [savingFleetMonitor, setSavingFleetMonitor] = useState(false);
  const [sendingFleetNow, setSendingFleetNow] = useState(false);
  const [fleetGroups, setFleetGroups] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [fleetFarm, setFleetFarm] = useState("");
  const [farmOptions, setFarmOptions] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchMachineTypes();
    fetchFleetProInfo();
    fetchFleetMonitorPhone();
    fetchFarms();
  }, []);

  const fetchFarms = async () => {
    try {
      const res = await axios.get(`${API}/machine-data/farms`);
      setFarmOptions(res.data?.farms || []);
    } catch (e) {
      console.error("Failed to load farms", e);
    }
  };

  const fetchFleetMonitorPhone = async () => {
    try {
      const res = await axios.get(`${API}/bot-settings`);
      setFleetMonitorPhone(res.data?.fleet_monitor_phone || "");
      setFleetFarm(res.data?.fleet_monitor_farm || "");
    } catch (e) {
      console.error("Failed to load bot settings", e);
    }
  };

  const saveFleetMonitorPhone = async () => {
    setSavingFleetMonitor(true);
    try {
      await axios.put(`${API}/bot-settings`, {
        fleet_monitor_phone: fleetMonitorPhone.trim(),
        fleet_monitor_farm: fleetFarm,
      });
      toast.success(fleetMonitorPhone.trim() ? "Fleet monitor number saved" : "Fleet monitor alerts turned off");
    } catch (e) {
      toast.error("Failed to save fleet monitor number");
    }
    setSavingFleetMonitor(false);
  };

  const loadFleetGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await axios.get(`${API}/bot/whatsapp-groups`);
      if (res.data?.success) {
        setFleetGroups(res.data.groups || []);
        if (!(res.data.groups || []).length) toast.info("No WhatsApp groups found on this instance");
      } else {
        toast.error(res.data?.message || "Could not load groups");
      }
    } catch (e) {
      toast.error("Could not load groups");
    }
    setLoadingGroups(false);
  };

  const sendFleetStatusNow = async () => {
    setSendingFleetNow(true);
    try {
      const res = await axios.post(`${API}/bot/fleet-monitor/send-now`);
      if (res.data?.success) toast.success(res.data.message || "Sent");
      else toast.error(res.data?.message || "Send failed");
    } catch (e) {
      toast.error("Send failed");
    }
    setSendingFleetNow(false);
  };

  const fetchFleetProInfo = async () => {
    try {
      const res = await axios.get(`${API}/downloads/mining-fleet-pro/info`);
      setFleetProInfo(res.data);
    } catch (e) {
      console.error("Failed to load Mining Fleet Pro info", e);
    }
  };

  const handleUploadFleetPro = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please choose a .zip file");
      return;
    }
    setUploadingFleetPro(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/downloads/mining-fleet-pro/upload`, formData);
      toast.success(`Uploaded — ${res.data.file_count} file(s), ${(res.data.size_bytes / 1024).toFixed(0)} KB`);
      fetchFleetProInfo();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFleetPro(false);
    }
  };

  const handleResetFleetPro = async () => {
    if (!window.confirm("Revert to the original built-in version? Your uploaded update will be removed.")) return;
    try {
      await axios.post(`${API}/downloads/mining-fleet-pro/reset`);
      toast.success("Reverted to the built-in version");
      fetchFleetProInfo();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const fleetProPublicLink = `${API}/downloads/mining-fleet-pro/public`;
  const handleCopyFleetProLink = () => {
    navigator.clipboard.writeText(fleetProPublicLink);
    toast.success("Link copied — share it with anyone, no login needed to download");
  };

  const fleetProMacPublicLink = `${API}/downloads/mining-fleet-pro-mac/public`;
  const handleCopyFleetProMacLink = () => {
    navigator.clipboard.writeText(fleetProMacPublicLink);
    toast.success("Link copied — share it with anyone, no login needed to download");
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchMachineTypes = async () => {
    try {
      const res = await axios.get(`${API}/machine-types`);
      setMachineTypes(res.data);
    } catch (e) {
      console.error("Failed to load machine types", e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Zcash IPs save immediately on add/remove so a refresh never loses them.
  const saveZcashIps = async (nextList) => {
    setSettings((prev) => ({ ...prev, zcash_ips: nextList, _new_zcash_ip: "" }));
    try {
      await axios.put(`${API}/settings`, { zcash_ips: nextList });
      toast.success("Zcash machines saved");
    } catch (e) {
      toast.error("Failed to save Zcash machines");
    }
  };

  const addZcashIp = () => {
    const v = (settings?._new_zcash_ip || "").trim();
    if (!v) return;
    const list = settings?.zcash_ips || [];
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast.info("Already in the list");
      return;
    }
    saveZcashIps([...list, v]);
  };

  const handleUpdateMachineType = async (mt) => {
    try {
      await axios.put(`${API}/machine-types/${mt.id}`, {
        name: mt.name,
        monthly_fee: mt.monthly_fee,
        daily_profit: mt.daily_profit || 0
      });
      toast.success(`Updated ${mt.name}`);
    } catch (e) {
      toast.error("Failed to update machine type");
    }
  };

  const updateMachineProfit = (id, value) => {
    setMachineTypes(machineTypes.map(mt => 
      mt.id === id ? { ...mt, daily_profit: parseFloat(value) || 0 } : mt
    ));
  };

  const [newMachine, setNewMachine] = useState({ name: "", monthly_fee: "", daily_profit: "" });

  const handleAddMachineType = async () => {
    const name = (newMachine.name || "").trim();
    if (!name) { toast.error("Enter a machine name"); return; }
    const fee = parseFloat(newMachine.monthly_fee);
    if (isNaN(fee) || fee < 0) { toast.error("Enter a valid hosting fee"); return; }
    try {
      await axios.post(`${API}/machine-types`, {
        name,
        monthly_fee: fee,
        daily_profit: parseFloat(newMachine.daily_profit) || 0,
      });
      toast.success(`Added ${name}`);
      setNewMachine({ name: "", monthly_fee: "", daily_profit: "" });
      fetchMachineTypes();
    } catch (e) {
      toast.error("Failed to add machine");
    }
  };

  const handleDeleteMachineType = async (mt) => {
    if (!window.confirm(`Delete machine type "${mt.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/machine-types/${mt.id}`);
      toast.success(`Deleted ${mt.name}`);
      fetchMachineTypes();
    } catch (e) {
      toast.error("Failed to delete machine");
    }
  };

  const handleReset = () => {
    setSettings({
      ...settings,
      message_template: `📢 Dear Valued Customer,

This is a kind reminder to please settle your hosting fees before the 2nd of each month, as we have major financial obligations to cover by that date.

Unlike other farms, we don't request payments 6 months in advance — but we kindly ask for your cooperation in making the payment on time each month.

🔹 {month} Hosting Fee: \${amount}
🔹 Payment Options:
• Whish: {whish}
• USDT (BEP20 Network): {usdt}

Thank you for your continued trust and support. Your timely payment helps us keep everything running smoothly.

Warm regards,
{team} 🐺💼`
    });
    toast.info("Message template reset to default");
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div className="skeleton h-12 w-48 rounded-xl" />
        <div className="skeleton h-64 rounded-xl" />
        <div className="skeleton h-32 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Settings</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Configure your WhatsApp message template</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#00E054] text-black hover:bg-[#00E054]/90 w-full sm:w-auto"
          data-testid="save-settings-btn"
        >
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Desktop Tools */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg bg-[#00E054]/10">
            <Monitor className="text-[#00E054]" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Mining Fleet Pro (Desktop Monitor)</h2>
            <p className="text-xs text-gray-500">Windows or Mac app for farm PCs — scans/monitors miners and syncs live data to this site</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => window.open(`${API}/downloads/mining-fleet-pro?admin_token=${encodeURIComponent(getAdminToken())}`, '_blank')}
            className="bg-[#00E054] text-black hover:bg-[#00E054]/90"
            data-testid="download-mining-fleet-pro-btn"
          >
            <Download size={16} className="mr-2" />
            Download for Windows
          </Button>

          <Button
            onClick={() => window.open(`${API}/downloads/mining-fleet-pro-mac?admin_token=${encodeURIComponent(getAdminToken())}`, '_blank')}
            variant="outline"
            className="border-[#27272A] hover:bg-[#1A1A1A]"
            data-testid="download-mining-fleet-pro-mac-btn"
          >
            <Download size={16} className="mr-2" />
            Download for Mac
          </Button>

          <label className="cursor-pointer">
            <input
              type="file"
              accept=".zip"
              onChange={handleUploadFleetPro}
              className="hidden"
              disabled={uploadingFleetPro}
              data-testid="upload-mining-fleet-pro-input"
            />
            <Button
              asChild
              variant="outline"
              disabled={uploadingFleetPro}
              className="border-[#27272A] hover:bg-[#1A1A1A]"
            >
              <span>
                <Upload size={16} className="mr-2" />
                {uploadingFleetPro ? "Uploading..." : "Upload New Version"}
              </span>
            </Button>
          </label>

          {fleetProInfo?.custom_version && (
            <Button
              onClick={handleResetFleetPro}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="reset-mining-fleet-pro-btn"
            >
              Revert to Built-in Version
            </Button>
          )}
        </div>

        <p className="text-xs text-gray-600 mt-2">
          <b>Windows:</b> unzip on the farm PC, then double-click "install_desktop.bat" first (installs required libraries), then run MiningFleet_Desktop.pyw.
        </p>
        <p className="text-xs text-gray-600 mt-1">
          <b>Mac:</b> unzip, then double-click "install_desktop_mac.command" first (installs required libraries — macOS may ask you to confirm running it once), then double-click "Launch Mining Fleet Pro.command" any time you want to open it.
        </p>
        <p className="text-xs text-gray-600 mt-1">
          To update the Windows version: zip the new version's files (the .pyw + .bat files, no folder inside) and upload the zip here — it replaces what "Download for Windows" gives out, and survives future site updates. The Mac download always ships the same underlying app code, so it stays in sync automatically.
        </p>
        {fleetProInfo?.custom_version ? (
          <p className="text-xs text-[#00C2FF] mt-2">
            ✓ Using your uploaded version ({fleetProInfo.file_count} file(s), {(fleetProInfo.size_bytes / 1024).toFixed(0)} KB) — uploaded {new Date(fleetProInfo.uploaded_at).toLocaleString()}
          </p>
        ) : (
          <p className="text-xs text-gray-600 mt-2">Currently serving the built-in version.</p>
        )}

        <div className="mt-4 pt-4 border-t border-[#27272A]">
          <div className="flex items-center gap-2 mb-2">
            <Link2 size={14} className="text-[#F59E0B]" />
            <p className="text-sm font-medium text-white">Public Share Link</p>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Anyone with this link can download it instantly — no admin login needed. Always sends whichever
            version is currently active above (built-in or your uploaded one).
          </p>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={fleetProPublicLink}
              onClick={(e) => e.target.select()}
              className="bg-[#0A0A0A] border-[#27272A] text-xs text-gray-400"
              data-testid="mining-fleet-pro-public-link"
            />
            <Button
              onClick={handleCopyFleetProLink}
              variant="outline"
              className="border-[#27272A] hover:bg-[#1A1A1A] shrink-0"
              data-testid="copy-mining-fleet-pro-link-btn"
            >
              <Copy size={16} className="mr-2" />
              Copy
            </Button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-[#27272A]">
          <div className="flex items-center gap-2 mb-2">
            <Link2 size={14} className="text-[#F59E0B]" />
            <p className="text-sm font-medium text-white">Public Share Link (Mac)</p>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Same idea, for the Mac build — anyone with this link can download it instantly, no admin login needed.
          </p>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={fleetProMacPublicLink}
              onClick={(e) => e.target.select()}
              className="bg-[#0A0A0A] border-[#27272A] text-xs text-gray-400"
              data-testid="mining-fleet-pro-mac-public-link"
            />
            <Button
              onClick={handleCopyFleetProMacLink}
              variant="outline"
              className="border-[#27272A] hover:bg-[#1A1A1A] shrink-0"
              data-testid="copy-mining-fleet-pro-mac-link-btn"
            >
              <Copy size={16} className="mr-2" />
              Copy
            </Button>
          </div>
        </div>
      </div>

      {/* Customer Portal Display */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-[#00C2FF]/10">
            <Thermometer className="text-[#00C2FF]" size={20} />
          </div>
          <h2 className="text-lg font-bold text-white">Customer Portal Display</h2>
        </div>

        <div className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
          <div className="flex items-center gap-3">
            {settings?.show_temperature !== false ? (
              <Eye size={18} className="text-[#00E054]" />
            ) : (
              <EyeOff size={18} className="text-gray-500" />
            )}
            <div>
              <p className="text-white font-medium">Show Temperature to Customers</p>
              <p className="text-xs text-gray-500">When off, temperature is hidden from the customer portal</p>
            </div>
          </div>
          <button
            onClick={() => setSettings({ ...settings, show_temperature: settings?.show_temperature === false ? true : false })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
              settings?.show_temperature !== false ? 'bg-[#00E054]' : 'bg-[#27272A]'
            }`}
            data-testid="show-temperature-toggle"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.show_temperature !== false ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Reboot Blocklist */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-[#FF4444]/10">
            <ShieldOff className="text-[#FF4444]" size={20} />
          </div>
          <h2 className="text-lg font-bold text-white">Protected Workers (No Reboot)</h2>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Workers whose name starts with any of these prefixes will NEVER be rebooted (manual or auto). They still appear in the watcher — if they crash, they stay crashed. Case-insensitive.
        </p>

        <div className="flex flex-wrap gap-2 mb-3">
          {(settings?.blocked_worker_prefixes || []).map((p, i) => (
            <span key={i} className="inline-flex items-center gap-2 bg-[#FF4444]/10 text-[#FF4444] border border-[#FF4444]/30 px-3 py-1 rounded-full text-sm">
              {p}
              <button
                onClick={() => {
                  const next = [...(settings.blocked_worker_prefixes || [])];
                  next.splice(i, 1);
                  setSettings({ ...settings, blocked_worker_prefixes: next });
                }}
                className="hover:text-white"
                data-testid={`remove-prefix-${p}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          {(!settings?.blocked_worker_prefixes || settings.blocked_worker_prefixes.length === 0) && (
            <span className="text-xs text-gray-600 italic">No prefixes — all workers can be rebooted</span>
          )}
        </div>

        <div className="flex gap-2">
          <Input
            placeholder="Add prefix (e.g. GOM)"
            value={settings?._new_prefix || ""}
            onChange={(e) => setSettings({ ...settings, _new_prefix: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const v = (settings._new_prefix || "").trim();
                if (!v) return;
                const list = settings.blocked_worker_prefixes || [];
                if (list.some(x => x.toLowerCase() === v.toLowerCase())) {
                  toast.info("Already in the list");
                  return;
                }
                setSettings({ ...settings, blocked_worker_prefixes: [...list, v], _new_prefix: "" });
              }
            }}
            className="bg-[#0A0A0A] border-[#27272A] text-white max-w-xs"
            data-testid="new-prefix-input"
          />
          <Button
            onClick={() => {
              const v = (settings._new_prefix || "").trim();
              if (!v) return;
              const list = settings.blocked_worker_prefixes || [];
              if (list.some(x => x.toLowerCase() === v.toLowerCase())) {
                toast.info("Already in the list");
                return;
              }
              setSettings({ ...settings, blocked_worker_prefixes: [...list, v], _new_prefix: "" });
            }}
            variant="outline"
            className="border-[#27272A] text-gray-300 hover:bg-[#1A1A1A]"
            data-testid="add-prefix-btn"
          >
            <Plus size={14} className="mr-1" /> Add
          </Button>
        </div>
        <p className="text-[11px] text-gray-600 mt-2">Don't forget to click "Save Changes" at the top after editing.</p>
      </div>

      {/* Zcash Machines (Sol/s) */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-[#00C2FF]/10">
            <Server className="text-[#00C2FF]" size={20} />
          </div>
          <h2 className="text-lg font-bold text-white">Zcash Machines (Sol/s)</h2>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Add the IP of any Zcash miner that the PC program syncs locally. Those machines will show their hashrate in Sol/s instead of GH/s, and won't be added into the GH/s combined total. You can type the full IP (192.168.20.188), just the ending (.188), or the last number (188).
        </p>

        <div className="flex flex-wrap gap-2 mb-3">
          {(settings?.zcash_ips || []).map((p, i) => (
            <span key={i} className="inline-flex items-center gap-2 bg-[#00C2FF]/10 text-[#00C2FF] border border-[#00C2FF]/30 px-3 py-1 rounded-full text-sm">
              {p}
              <button
                onClick={() => {
                  const next = [...(settings.zcash_ips || [])];
                  next.splice(i, 1);
                  saveZcashIps(next);
                }}
                className="hover:text-white"
                data-testid={`remove-zcash-ip-${p}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          {(!settings?.zcash_ips || settings.zcash_ips.length === 0) && (
            <span className="text-xs text-gray-600 italic">No Zcash IPs — all local miners show GH/s</span>
          )}
        </div>

        <div className="flex gap-2">
          <Input
            placeholder="Add IP (e.g. 192.168.20.188 or .188)"
            value={settings?._new_zcash_ip || ""}
            onChange={(e) => setSettings({ ...settings, _new_zcash_ip: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addZcashIp();
              }
            }}
            className="bg-[#0A0A0A] border-[#27272A] text-white max-w-xs"
            data-testid="new-zcash-ip-input"
          />
          <Button
            onClick={addZcashIp}
            variant="outline"
            className="border-[#27272A] text-gray-300 hover:bg-[#1A1A1A]"
            data-testid="add-zcash-ip-btn"
          >
            <Plus size={14} className="mr-1" /> Add
          </Button>
        </div>
        <p className="text-[11px] text-gray-600 mt-2">This list saves automatically when you add or remove an IP — no need to click "Save Changes".</p>
      </div>

      {/* Payment Options */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-[#00E054]/10">
            <CreditCard className="text-[#00E054]" size={20} />
          </div>
          <h2 className="text-lg font-bold text-white">Payment Options</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Whish Number</Label>
            <Input
              value={settings?.whish_number || ""}
              onChange={(e) => setSettings({ ...settings, whish_number: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              placeholder="03022005"
              data-testid="whish-input"
            />
          </div>
          <div>
            <Label>USDT Address (BEP20)</Label>
            <Input
              value={settings?.usdt_address || ""}
              onChange={(e) => setSettings({ ...settings, usdt_address: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1 font-mono text-sm"
              placeholder="0x..."
              data-testid="usdt-input"
            />
          </div>
        </div>
      </div>

      <WebhookLock />
      <WhatsAppBackup />

      {/* Fleet Monitor — offline/recovery alerts to a second number */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-[#F59E0B]/10">
            <MessageSquare className="text-[#F59E0B]" size={20} />
          </div>
          <h2 className="text-lg font-bold text-white">Fleet Monitor</h2>
        </div>

        <p className="text-sm text-gray-400 mb-3">
          A second WhatsApp number that receives only the machine OFFLINE and back-ONLINE
          alerts. Driven by the live monitor (the PC sync), so it covers every machine on
          the farm — not just the ones under a ViaBTC watcher key. It gets nothing else:
          no reminders, no receipts, no bot replies. Leave empty to turn it off.
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] max-w-sm">
            <Label>WhatsApp Number or Group</Label>
            <Input
              value={fleetMonitorPhone}
              onChange={(e) => setFleetMonitorPhone(e.target.value)}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              placeholder="+961XXXXXXXX"
              data-testid="fleet-monitor-input"
            />
          </div>
          <Button
            onClick={saveFleetMonitorPhone}
            disabled={savingFleetMonitor}
            className="bg-[#00E054] text-black hover:bg-[#00C048]"
            data-testid="fleet-monitor-save"
          >
            {savingFleetMonitor ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outline"
            onClick={sendFleetStatusNow}
            disabled={sendingFleetNow || !fleetMonitorPhone.trim()}
            className="border-[#27272A]"
            data-testid="fleet-monitor-send-now"
          >
            {sendingFleetNow ? "Sending..." : "Send Now"}
          </Button>
          <Button
            variant="outline"
            onClick={loadFleetGroups}
            disabled={loadingGroups}
            className="border-[#27272A]"
            data-testid="fleet-monitor-groups"
          >
            {loadingGroups ? "Loading..." : "Find Groups"}
          </Button>
        </div>

        <div className="mt-3 max-w-sm">
          <Label>Farm</Label>
          <select
            value={fleetFarm}
            onChange={(e) => setFleetFarm(e.target.value)}
            className="w-full mt-1 bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 text-sm text-white"
            data-testid="fleet-monitor-farm"
          >
            <option value="">All farms</option>
            {farmOptions.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name} ({f.machines})
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Alerts cover only this farm. Saved by the Save button above.
          </p>
        </div>

        {fleetGroups && fleetGroups.length > 0 && (
          <div className="mt-3 border border-[#27272A] rounded-lg divide-y divide-[#27272A] max-h-56 overflow-y-auto">
            {fleetGroups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => {
                  setFleetMonitorPhone(g.id);
                  toast.info(`Selected "${g.name}" — click Save`);
                }}
                className="w-full text-left px-3 py-2 hover:bg-[#18181B] transition-colors"
              >
                <div className="text-sm text-white">{g.name}</div>
                <div className="text-xs text-gray-500 font-mono">{g.id}</div>
              </button>
            ))}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-2">
          Saves on its own — no need to click "Save Changes" at the top.
          <br />
          For a group, click "Find Groups" and pick one — a group ID ends in{" "}
          <span className="font-mono">@g.us</span> and can't be typed from memory.
          <br />
          "Send Now" messages the current offline list on demand. Read-only — it can never
          suppress a real alert.
        </p>
      </div>

      {/* Team Info */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-[#7C3AED]/10">
            <Users className="text-[#7C3AED]" size={20} />
          </div>
          <h2 className="text-lg font-bold text-white">Team Info</h2>
        </div>
        
        <div>
          <Label>Team Name</Label>
          <Input
            value={settings?.team_name || ""}
            onChange={(e) => setSettings({ ...settings, team_name: e.target.value })}
            className="bg-[#0A0A0A] border-[#27272A] mt-1"
            placeholder="WKBeast Team"
            data-testid="team-name-input"
          />
        </div>
      </div>

      {/* Machine Types - Daily Profit */}
      <div className="card p-4 sm:p-6">
        <div className="page-header mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#F59E0B]/10">
              <Cpu className="text-[#F59E0B]" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Machine Profit Estimates</h2>
              <p className="text-xs text-gray-500">Daily profit per machine (from asicminervalue.com)</p>
              <p className="text-xs text-gray-600 mt-1">Each machine has its own green "Save" button below — the "Save Changes" button at the top of this page does NOT save these.</p>
            </div>
          </div>
          <a
            href="https://www.asicminervalue.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-[#00C2FF] hover:underline flex items-center gap-1"
          >
            View Live Prices <ExternalLink size={12} />
          </a>
        </div>
        
        <div className="space-y-3">
          {machineTypes.map(mt => (
            <div key={mt.id} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{mt.name}</p>
                <p className="text-xs text-gray-500">Hosting: ${mt.monthly_fee}/mo</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <DollarSign size={14} className="text-[#00E054] shrink-0" />
                <Input
                  type="number"
                  step="0.01"
                  value={mt.daily_profit || 0}
                  onChange={(e) => updateMachineProfit(mt.id, e.target.value)}
                  className="w-20 sm:w-24 bg-[#1A1A1A] border-[#27272A] text-right"
                  placeholder="0.00"
                />
                <span className="text-xs text-gray-500">/day</span>
                <Button
                  size="sm"
                  onClick={() => handleUpdateMachineType(mt)}
                  className="bg-[#00E054] text-black ml-auto sm:ml-0"
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteMachineType(mt)}
                  className="border-[#27272A] text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  title="Delete this machine type"
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Add new machine type */}
        <div className="mt-4 p-3 bg-[#0A0A0A] rounded-lg border border-dashed border-[#27272A]">
          <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
            <Plus size={12} /> Add a new machine
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={newMachine.name}
              onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })}
              className="bg-[#1A1A1A] border-[#27272A] flex-1"
              placeholder="Model name (e.g. L7, Ks5pro)"
            />
            <Input
              type="number"
              step="0.01"
              value={newMachine.monthly_fee}
              onChange={(e) => setNewMachine({ ...newMachine, monthly_fee: e.target.value })}
              className="bg-[#1A1A1A] border-[#27272A] w-full sm:w-32"
              placeholder="Hosting $/mo"
            />
            <Input
              type="number"
              step="0.01"
              value={newMachine.daily_profit}
              onChange={(e) => setNewMachine({ ...newMachine, daily_profit: e.target.value })}
              className="bg-[#1A1A1A] border-[#27272A] w-full sm:w-32"
              placeholder="Profit $/day"
            />
            <Button onClick={handleAddMachineType} className="bg-[#00E054] text-black">
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
        </div>
        
        <p className="text-xs text-gray-500 mt-3">
          These values are shown to customers in their portal as "Estimated Monthly Earnings"
        </p>
      </div>

      {/* Message Template */}
      <div className="card p-4 sm:p-6">
        <div className="page-header mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#25D366]/10">
              <MessageSquare className="text-[#25D366]" size={20} />
            </div>
            <h2 className="text-lg font-bold text-white">Message Template</h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="border-[#27272A] hover:bg-[#1A1A1A] w-full sm:w-auto"
            data-testid="reset-template-btn"
          >
            <RotateCcw size={14} className="mr-2" />
            Reset to Default
          </Button>
        </div>
        
        <div>
          <Label>WhatsApp Message</Label>
          <textarea
            value={settings?.message_template || ""}
            onChange={(e) => setSettings({ ...settings, message_template: e.target.value })}
            className="w-full mt-1 p-4 bg-[#0A0A0A] border border-[#27272A] rounded-lg text-white resize-none h-80 font-mono text-sm"
            placeholder="Enter your message template..."
            data-testid="message-template-input"
          />
          
          <div className="mt-3 p-3 bg-[#1A1A1A] rounded-lg border border-[#27272A]">
            <p className="text-xs text-gray-400 mb-2">Available placeholders:</p>
            <div className="flex flex-wrap gap-2">
              {['{month}', '{amount}', '{whish}', '{usdt}', '{team}'].map(placeholder => (
                <code key={placeholder} className="px-2 py-1 bg-[#0A0A0A] rounded text-xs text-[#00E054]">
                  {placeholder}
                </code>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg font-bold text-white mb-4">Message Preview</h2>
        <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A] whitespace-pre-wrap text-sm">
          {settings?.message_template
            ?.replace('{month}', 'January 2026')
            .replace('{amount}', '300')
            .replace('{whish}', settings?.whish_number || '03022005')
            .replace('{usdt}', settings?.usdt_address || '0x...')
            .replace('{team}', settings?.team_name || 'WKBeast Team')
          }
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
